import React, { useContext, useEffect, useState, useLayoutEffect } from 'react';
import {
  AppHeader,
  Box,
  Button,
  ButtonGroup,
  Form,
  Radio,
  RadioGroup,
  RadioTypography,
  Stack,
  Typography,
  useRadioGroupValidator,
} from '@ace-clublabs/shared-component-library';
import { useHistory } from 'react-router-dom';
import holiday_gift_image from '../assets/holiday-gift-image.svg';
import regular_gift_image from '../assets/regular-gift-image.svg';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { AssociateInfo } from '../application/models/NewJoinSessionData';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { UtagType } from '../utils/UtagHelper';
import { GiftMemberTypes, Pages } from '@lib/types';
import { getNextPage } from '../utils/getNextPage';
import { getProgressProps } from '../utils/helpers';
import { AppProgressHeader } from '../components/AppProgressHeader';

interface LocationState {
  associate?: AssociateInfo;
  isEditing?: boolean;
}

export const SendGiftCardPage: React.FC = () => {
  const history = useHistory();

  const {
    membershipInfo,
    refreshSession,
    loadingInfo,
    sessionData,
    sendTag,
    updateMembershipForm,
    submitMembershipForm,
    getNewBusinessCost,
    loadingProvider,
  } = useContext(MembershipAppStateContext);

  const { formData } = sessionData;
  const { giftDetails } = formData;
  const giftCardValue = membershipInfo?.giftDetails?.giftCardValue;
  const useHolidayImage = membershipInfo?.giftDetails?.isHoliday;

  // const location = useLocation<LocationState>();
  const radioValidator = useRadioGroupValidator();
  const [isTagging, setIsTagging] = useState(true);

  const [sendGiftCardTo, setSendGiftCardTo] = useState<GiftMemberTypes | undefined>(giftDetails?.giftCardRecipient);

  const progressProps = getProgressProps(sessionData.flowType!, Pages.SendGiftCardPage);

  useEffect(() => {
    const updatedFormData = {
      ...sessionData.formData,
      giftDetails: {
        ...sessionData.formData.giftDetails,
        giftCardRecipient: sendGiftCardTo,
      },
    };
    updateMembershipForm(updatedFormData);
  }, [sendGiftCardTo]);

  const submitSendGiftToOnClick = async () => {
    try {
      loadingProvider.startLoading('Saving your membership information');
      //Save Gift Card Recipient, Membership Card Recipient, Bill Recipient and Greeting to backend Cache
      await submitMembershipForm(true, true);
      //Save associate info to the back end
      await getNewBusinessCost();
      loadingProvider.endLoading();
      history.push(getNextPage(Pages.SendGiftCardPage, sessionData.flowType));
    } catch (e) {
      const isBotDetectionResponse =
        // @ts-ignore
        e?.error?.response?.status === 403 && !!e.error?.response?.data?.pxEvent;
      if (isBotDetectionResponse) {
        loadingProvider.endLoading();
        window._pxOnCaptchaSuccess = function (isValid) {
          if (isValid) {
            submitSendGiftToOnClick();
          }
        };
      } else {
        history.push(Pages.SorryPage);
      }
    }
  };

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo) {
      return;
    }
    //update!!!!!!
    sendTag(UtagType.view, 'membership:gift:gift card');
    setIsTagging(false);
  }, [membershipInfo]);

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;

  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <Box className="send-gift-to-view">
      <AppProgressHeader {...progressProps} styleOverrides={{ marginBottom: '1rem' }} />

      <Stack className="send-gift-header-section">
        <img
          src={useHolidayImage ? holiday_gift_image : regular_gift_image}
          className={`gift-image ${useHolidayImage ? 'holiday-gift-card' : 'regular-gift-card'}`}
        ></img>
        <Typography className="send-gift-title" variant="h5Medium">
          Where should we send the ${giftCardValue} gift card?
        </Typography>
      </Stack>
      <Form
        onSubmit={submitSendGiftToOnClick}
        sx={{
          '& .select-where-to-send': { width: '100%' },
          '& .select-where-to-send .MuiFormControlLabel-root': { width: '100%' },
        }}
      >
        <RadioGroup
          className="select-where-to-send"
          label="Select one"
          validator={radioValidator}
          value={
            sendGiftCardTo === GiftMemberTypes.RECIPIENT
              ? 'giftRecipient'
              : sendGiftCardTo === GiftMemberTypes.DONOR
                ? 'giftDonor'
                : null
          }
        >
          <Radio
            label={<RadioTypography>Gift recipient</RadioTypography>}
            value="giftRecipient"
            id="sendToGiftRecipient"
            onClick={() => {
              setSendGiftCardTo(GiftMemberTypes.RECIPIENT);
            }}
          />
          <Radio
            label={<RadioTypography>Me</RadioTypography>}
            value="giftDonor"
            id="sendToDonor"
            onClick={() => {
              setSendGiftCardTo(GiftMemberTypes.DONOR);
            }}
          />
        </RadioGroup>
        <ButtonGroup>
          <Button id="nextButton" type="submit" className="btn-large" label="Next" />
        </ButtonGroup>
      </Form>
    </Box>
  );
};
